import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert } from "@/components/ui/alert";
import { AdminList, AdminListRow } from "@/modules/shell/AdminList";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import {
  getBranchAccess,
  setMemberBranches,
  type BranchAccessMember,
} from "@/shared/api/branches";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";

/** Owner Settings: user ↔ branch allow-list — plan/24 */
export function BranchAccessPage() {
  const { companySlug } = useParams();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const accessQuery = useQuery({
    queryKey: queryKeys.branches.access(companySlug ?? ""),
    enabled: Boolean(companySlug),
    queryFn: () => getBranchAccess(companySlug!),
  });
  const errorMsg = queryErrorMessage(accessQuery.error, accessQuery.isError);
  useAuthRedirectOnQueryError(errorMsg);

  if (!companySlug) return null;

  const branches = accessQuery.data?.branches ?? [];
  const members = accessQuery.data?.members ?? [];
  const showPicker = Boolean(accessQuery.data?.showPicker);
  const displayError =
    error ??
    (errorMsg && errorMsg !== "Not signed in" ? errorMsg : null);

  async function saveMember(
    m: BranchAccessMember,
    next: { branchIds: string[]; unrestricted: boolean },
  ) {
    setBusy(true);
    setError(null);
    try {
      await setMemberBranches(companySlug!, m.userId, next);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.branches.access(companySlug!),
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  return (
      <PageFrame>
      <PageHeader
        title="Branch access"
        description={
          showPicker
            ? "Assign which branches each user can open (POS terminals follow)."
            : "Single branch — picker hidden in the app. Assignments still stored."
        }
      />

      {displayError && (
        <Alert variant="destructive" className="mt-4">
          {displayError}
        </Alert>
      )}

      {accessQuery.isPending && (
        <Alert variant="muted" className="mt-8">
          Loading branch access…
        </Alert>
      )}

      {!accessQuery.isPending && members.length === 0 && !displayError ? (
        <div className="mt-8">
          <EmptyState title="No members" description="Invite people first." />
        </div>
      ) : !accessQuery.isPending && members.length > 0 ? (
        <AdminList>
          {members.map((m) => (
            <AdminListRow
              key={m.userId}
              className="flex flex-col items-stretch gap-0 px-4 py-4"
            >
              <div className="font-medium">
                {m.displayName ?? m.email ?? m.userCode ?? m.userId.slice(0, 8)}
              </div>
              <div className="text-xs text-[var(--color-muted)]">
                {m.email ?? m.userCode ?? "member"}
              </div>
              <label className="mt-3 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={m.unrestricted}
                  disabled={busy}
                  onChange={(e) =>
                    void saveMember(m, {
                      branchIds: e.target.checked ? [] : m.branchIds,
                      unrestricted: e.target.checked,
                    })
                  }
                />
                All branches
              </label>
              {!m.unrestricted && (
                <div className="mt-2 flex flex-col gap-1">
                  {branches.map((b) => {
                    const checked = m.branchIds.includes(b.id);
                    return (
                      <label
                        key={b.id}
                        className="flex items-center gap-2 text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={busy}
                          onChange={() => {
                            const next = checked
                              ? m.branchIds.filter((id) => id !== b.id)
                              : [...m.branchIds, b.id];
                            void saveMember(m, {
                              branchIds: next,
                              unrestricted: false,
                            });
                          }}
                        />
                        {b.name}
                        <span className="text-xs text-[var(--color-muted)]">
                          {b.slug}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </AdminListRow>
          ))}
        </AdminList>
      ) : null}

      <p className="mt-8 flex flex-wrap gap-3 text-sm">
        <Link
          to={`/c/${companySlug}/settings/branches`}
          className="no-underline"
        >
          ← Branches
        </Link>
        <Link to={`/c/${companySlug}`} className="no-underline">
          Company
        </Link>
      </p>
    </PageFrame>
  );
}
